import { Banner } from 'plaid-threads';
import React, { Dispatch, ReactNode, useEffect } from 'react';
import { CookiesProvider } from 'react-cookie/es6';
import { useHistory, useLocation } from 'react-router-dom';
import { createReducer } from 'react-use';

import AIS from './AIS';
import AISV2 from './AISV2';
import './App.scss';
import Consents from './Consents';
import Header from './Header';
import Landing from './Landing';
import LandingV2 from './LandingV2';
import MobileOAuth from './MobileOAuth';
import PIS from './PIS';
import PISV2 from './PISV2';
import UpdateMode from './UpdateMode';
import AppAction from './actionTypes';
import { PageType } from './constants';
import { getPageFromPath } from './routeConfig';
import AppReducer, {
  AppState,
  InitialState,
  stateInitializer,
} from './reducer';

interface AppContextShape extends AppState {
  // no actions yet
  actions: {};
  dispatch: Dispatch<AppAction>;
}

export const AppContext = React.createContext<AppContextShape>(
  InitialState as AppContextShape,
);

export const ReducerMiddleware =
  (store: { getState: () => AppState }) =>
  (next: (action: AppAction) => void) =>
  (action: AppAction) => {
    next(action);
    const nextState = store.getState();
    saveToLocalStorage(nextState);
  };

export const useReducer = createReducer(ReducerMiddleware);

function saveToLocalStorage(appState: AppState) {
  try {
    localStorage.setItem('APP_STATE', JSON.stringify(appState));
  } catch (e) {
    console.warn(e);
  }
}

function loadFromLocalStorage() {
  try {
    const localAppState = localStorage.getItem('APP_STATE');
    if (localAppState == null) return InitialState;
    const stateFromStorage = JSON.parse(localAppState);
    let countryCode = stateFromStorage?.countryCode;
    if (typeof countryCode === 'string') {
      // temporary to handle migration of countryCode from
      // `string` to `array` of strings;
      countryCode = [countryCode];
    }

    return {
      ...InitialState,
      ...stateFromStorage,
      page: 'landing', // overwrite page state to always redirect back to the landing page
      successfulExtraction: false,
      countryCode,
      accountType: null, // Always set account type to null when loading from local storage
      customizationName: null, // Reset customization name as well
    };
  } catch (e) {
    console.warn(e);
    return InitialState;
  }
}

interface AppProps {
  initialPage?: string;
}

export const AppProvider: React.FC<{
  children: ReactNode;
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}> = (props) => {
  const { state, dispatch } = props;

  return (
    <AppContext.Provider value={{ ...state, actions: {}, dispatch }}>
      {props.children}
    </AppContext.Provider>
  );
};

const App: React.FC<AppProps> = (props) => {
  const [state, dispatch] = useReducer(
    AppReducer,
    loadFromLocalStorage(),
    stateInitializer,
  );
  const { page, successfulExtraction } = state;
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const pageFromUrl = getPageFromPath(location.pathname);
    if (pageFromUrl) {
      dispatch({
        type: 'SET_PAGE',
        payload: { page: pageFromUrl },
      });
    } else if (props.initialPage) {
      dispatch({
        type: 'SET_PAGE',
        payload: {
          page: props.initialPage as PageType,
        },
      });
    }
    // Only run on mount — URL and initialPage are read once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const unlisten = history.listen((loc) => {
      const pageFromUrl = getPageFromPath(loc.pathname);
      if (pageFromUrl) {
        dispatch({ type: 'SET_PAGE', payload: { page: pageFromUrl } });
      } else if (
        loc.pathname !== '/demo' &&
        loc.pathname !== '/demo/oauth'
      ) {
        dispatch({ type: 'SET_PAGE', payload: { page: 'landingV2' } });
      }
    });
    return unlisten;
  }, [history, dispatch]);

  let component;
  let appContainerClass = 'App__container';
  let appBannerClass = 'App__banner';
  if (page === 'landing') {
    component = <Landing />;
  } else if (page === 'pis') {
    component = <PIS />;
  } else if (page === 'ais') {
    component = <AIS />;
  } else if (page === 'landingV2') {
    // external facing demo
    appContainerClass = 'App__containerV2';
    appBannerClass = 'App__banner_none';
    component = <LandingV2 />;
  } else if (page === 'pisV2') {
    // external facing demo
    appContainerClass = 'App__containerV2';
    appBannerClass = 'App__banner_none';
    component = (
      <div className="innerContainer">
        <PISV2 />
      </div>
    );
  } else if (page === 'aisV2') {
    // external facing demo
    appContainerClass = 'App__containerV2';
    appBannerClass = 'App__banner_none';
    component = <AISV2 />;
  } else if (page === 'oauth') {
    component = <MobileOAuth />;
  } else if (page === 'consents') {
    component = <Consents />;
  } else if (page === 'updatemode') {
    component = <UpdateMode />;
  } else {
    throw new Error('Invalid Page');
  }

  const bannerDescription = () => {
    switch (page) {
      case 'ais':
        return 'Live Production Demo: AISP';
      case 'pis':
        return 'Live Production Demo: PISP';
      default:
        return 'Live Production Demo';
    }
  };

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const key = params.get('key');
  const isM2020 = key === 'm2020';

  return (
    <AppProvider state={state} dispatch={dispatch}>
      <CookiesProvider>
        <div className="App">
          {!isM2020 && <Header isM2020={false} />}
          <div className={appContainerClass}>{component}</div>
          {!successfulExtraction && (
            <Banner className={appBannerClass}>{bannerDescription()}</Banner>
          )}
        </div>
      </CookiesProvider>
    </AppProvider>
  );
};

export default App;
